import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { GlobalSearch } from './components/GlobalSearch'
import { AuthGate } from './components/auth/AuthGate'
import { AppLayout } from './components/layout/AppLayout'
import { InstallPrompt } from './components/InstallPrompt'
import { LoginPage } from './pages/LoginPage'
import { HomePage } from './pages/HomePage'
import { IncidentsListPage } from './pages/IncidentsListPage'
import { IncidentDetailPage } from './pages/IncidentDetailPage'
import { OnCallPage } from './pages/OnCallPage'
import { ScheduleDetailPage } from './pages/ScheduleDetailPage'
import { EscalationPolicyDetailPage } from './pages/EscalationPolicyDetailPage'
import { AlertDetailPage } from './pages/AlertDetailPage'
import { PostMortemTemplatesPage } from './pages/PostMortemTemplatesPage'
import { SettingsUsersPage } from './pages/SettingsUsersPage'
import { SystemSettingsPage } from './pages/SystemSettingsPage'
import { SettingsMigrationsPage } from './pages/SettingsMigrationsPage'
import { IntegrationsPage } from './pages/IntegrationsPage'
import { LogoutPage } from './pages/LogoutPage'
import { SetupWizardPage } from './pages/SetupWizardPage'
import { StatusApp } from './components/status/StatusApp'

/** Redirect /escalation-policies/:id → /on-call/escalation-paths/:id */
function EscalationPoliciesRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/on-call/escalation-paths/${id ?? ''}`} replace />
}

function App() {
  const [showSearch, setShowSearch] = useState(false)

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearch(true)
      }
    }
    const handleOpenSearch = () => setShowSearch(true)
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('open-search', handleOpenSearch)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      window.removeEventListener('open-search', handleOpenSearch)
    }
  }, [])

  // Register the service worker on first load (production only).
  useEffect(() => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
          if (import.meta.env.DEV) {
            console.log('[sw] registered', reg.scope)
          }
        })
        .catch((err) => {
          console.error('[sw] registration failed', err)
        })
    }
  }, [])

  return (
    <BrowserRouter>
      <AuthProvider>
        <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
        {/* PWA install banner — only rendered on mobile when browser supports it */}
        <InstallPrompt />
        <AuthGate>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Shown after successful sign-out — always accessible, bypasses AuthGate */}
            <Route path="/logout" element={<LogoutPage />} />

            {/* Public status page — accessible without auth (OPE-111) */}
            <Route path="/status/*" element={<StatusApp base="/status" />} />

            {/* Full-page onboarding wizard — requires auth, no sidebar */}
            <Route path="/setup" element={<SetupWizardPage />} />

            <Route element={<AppLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/incidents" element={<IncidentsListPage />} />
              <Route path="/incidents/:id" element={<IncidentDetailPage />} />

              {/* On-call unified section */}
              <Route path="/on-call" element={<OnCallPage />} />
              <Route path="/on-call/escalation-paths" element={<OnCallPage />} />
              <Route path="/on-call/schedules" element={<OnCallPage />} />
              {/* Detail pages (nested under on-call) */}
              <Route path="/on-call/escalation-paths/:id" element={<EscalationPolicyDetailPage />} />
              <Route path="/on-call/:id" element={<ScheduleDetailPage />} />

              {/* Backwards-compat redirects */}
              <Route path="/routing-rules" element={<Navigate to="/on-call" replace />} />
              <Route path="/escalation-policies" element={<Navigate to="/on-call/escalation-paths" replace />} />
              <Route path="/escalation-policies/:id" element={<EscalationPoliciesRedirect />} />

              <Route path="/alerts/:id" element={<AlertDetailPage />} />
              <Route path="/post-mortem-templates" element={<PostMortemTemplatesPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/settings" element={<Navigate to="/settings/users" replace />} />
              <Route path="/settings/users" element={<SettingsUsersPage />} />
              <Route path="/settings/system" element={<SystemSettingsPage />} />
              <Route path="/settings/migrations" element={<SettingsMigrationsPage />} />
            </Route>
          </Routes>
        </AuthGate>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
